import { encounterCreatureField } from './array-types';
import { fromBaseRecord } from '../janus-engine/compile/from-base-context';

const key = (encounterId: string, actorId: string): string => encounterId.toUpperCase() + ':' + actorId.toUpperCase();

/** Shared page state: spawn actions and the resource watcher live in separate bundles. */
export function beginSpawnSave(encounterId: string, actorIds: readonly string[]): (saved?: boolean) => void {
    const pending = window.ftPendingSpawnSaves ??= new Set<string>();
    const keys = actorIds.map(id => key(encounterId, id));
    for (const id of keys) pending.add(id);
    return (saved = true) => {
        keys.forEach((id, i) => {
            pending.delete(id);
            if (saved) window.ftFailedSpawnSaves?.delete(id);
            else {
                const cache = (window as unknown as { FateTide?: { encounterCache?: Map<string, unknown> } }).FateTide?.encounterCache;
                const encounter = cache?.get(encounterId.toUpperCase());
                const roster = fromBaseRecord(encounter) && Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [];
                const row = roster.find((row: unknown) => fromBaseRecord(row)
                    && String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id')).toUpperCase() === actorIds[i]!.toUpperCase());
                if (row) (window.ftFailedSpawnSaves ??= new Map<string, unknown>()).set(id, row);
            }
        });
        window.dispatchEvent(new CustomEvent('ft:spawn-save-settled', { detail: { encounterId, actorIds, saved } }));
    };
}

/** A failed optimistic row cannot save resources; a later saved row with the same id can. */
export function spawnSavePending(encounterId: string, actorId: string, row: unknown): boolean {
    const id = key(encounterId, actorId);
    if (window.ftPendingSpawnSaves?.has(id)) return true;
    const failed = window.ftFailedSpawnSaves?.get(id);
    if (failed && failed !== row) window.ftFailedSpawnSaves?.delete(id);
    return failed !== undefined && failed === row;
}

/** A saved paint can update the same host in place after a lost birth receipt. */
export function reconcileSpawnSaves(encounterId: string, actorIds: readonly string[]): void {
    const released = actorIds.filter(id => window.ftFailedSpawnSaves?.delete(key(encounterId, id)));
    if (released.length) window.dispatchEvent(new CustomEvent('ft:spawn-save-settled', { detail: { encounterId, actorIds: released, saved: true } }));
}

declare global {
    interface Window { ftPendingSpawnSaves?: Set<string>; ftFailedSpawnSaves?: Map<string, unknown> }
}
